/**
 * DealContractsTab - Aba de contratos no modal do deal
 * Lista contratos existentes e permite gerar novos
 * Usa ContractsTableView compartilhado (mesmo padrão visual da aba Negócios)
 * 
 * RELEASE: v2.0.0 - 2026-03-15
 * v1.1.0: Subscription Realtime movida para cá (sempre montada quando aba ativa)
 * v1.2.0: Subscription em contract_signatures para invalidar a lista
 * v2.0.0: Migração para layout de tabela unificado (ContractsTableView)
 */
import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useContractsByDeal } from '@/hooks/useContracts';
import { ContractWizardDialog } from './wizard/ContractWizardDialog';
import { ContractDetailSheet } from './ContractDetailSheet';
import { ContractsTableView } from './ContractsTableView';
// v2.2.0: trava do processo consultada antes do clique
import { useWorkflowGuardCheck } from '@/hooks/useWorkflowGuardCheck';
import { WorkflowGuardGate } from '@/components/workflow/WorkflowGuardGate';

// Props do componente
interface DealContractsTabProps {
  /** ID do deal */
  dealId: string;
  /** ID do tenant */
  tenantId: string;
  /** ID do contato vinculado ao deal */
  contactId?: string | null;
  /** Se a aba está ativa (para lazy loading) */
  isActive?: boolean;
  /** v2.1.0: Permissão para gerar/criar novo contrato (crm_contracts.can_create) */
  canCreate?: boolean;
  /** v2.1.0: Permissão para editar contrato (repassada ao ContractDetailSheet) */
  canEdit?: boolean;
  /** v2.1.0: Permissão para excluir/cancelar contrato */
  canDelete?: boolean;
}

/**
 * Aba de contratos dentro do modal do deal
 */
export function DealContractsTab({
  dealId,
  tenantId,
  contactId,
  isActive,
  canCreate = true,
  canEdit = true,
  canDelete = true,
}: DealContractsTabProps) {
  // Estado do wizard de geração de contrato
  const [wizardOpen, setWizardOpen] = useState(false);
  // Estado do sheet de detalhes do contrato
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);

  // v1.1.0: QueryClient para atualização otimista do cache da lista
  const queryClient = useQueryClient();

  // Busca contratos do deal
  const { data: contracts, isLoading } = useContractsByDeal(isActive ? dealId : null);

  // v2.2.0: política do processo para "Gerar contrato" (decisão real segue no banco)
  const guardGerarContrato = useWorkflowGuardCheck({
    actionCode: 'crm.contract.generate',
    rootEntityType: 'crm_deal',
    rootEntityId: dealId,
    enabled: Boolean(isActive && canCreate),
  });

  // v1.1.0: Realtime na lista — escuta mudanças de status sem polling
  useEffect(() => {
    if (!dealId || !isActive) return;

    // Sufixo único por instância: evita conflito de canal em modais de negócio empilhados
    const channel = supabase
      .channel(`deal-contracts-list-${dealId}-${Math.random().toString(36).slice(2, 10)}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contracts',
          filter: `deal_id=eq.${dealId}`,
        },
        (payload) => {
          const newData = payload.new as Record<string, unknown>;
          queryClient.setQueryData(
            ['contracts', 'deal', dealId],
            (old: any[] | undefined) =>
              old?.map(c => {
                if (c.id !== newData.id) return c;
                return {
                  ...c,
                  ...newData,
                  contract_templates: c.contract_templates,
                  contract_signatures: c.contract_signatures,
                };
              })
          );
          const finalStatuses = ['signed', 'cancelled', 'error'];
          if (finalStatuses.includes(newData.status as string)) {
            queryClient.invalidateQueries({
              queryKey: ['contracts', 'deal', dealId],
            });
          }
        }
      )
      // v1.2.0: Escuta mudanças nos signatários para atualizar contagem
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contract_signatures',
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ['contracts', 'deal', dealId],
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [dealId, isActive, queryClient]);

  return (
    <div className="flex flex-col h-full min-h-0 space-y-3">
      {/* Header com botão de criar — só renderiza se tem permissão */}
      {canCreate && (
        <div className="flex items-center justify-end flex-shrink-0">
          <WorkflowGuardGate decisao={guardGerarContrato}>
            <Button
              size="sm"
              onClick={() => setWizardOpen(true)}
              className="h-8 text-xs gap-1.5"
              disabled={guardGerarContrato.bloqueado}
            >
              <Plus className="h-3.5 w-3.5" />
              Gerar Contrato
            </Button>
          </WorkflowGuardGate>
        </div>
      )}

      {/* Tabela compartilhada sem coluna de negócio */}
      <ContractsTableView
        contracts={contracts}
        isLoading={isLoading}
        showDealColumn={false}
        onSelectContract={(id) => setSelectedContractId(id)}
        emptyAction={
          canCreate ? (
            <WorkflowGuardGate decisao={guardGerarContrato}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setWizardOpen(true)}
                className="text-xs gap-1.5"
                disabled={guardGerarContrato.bloqueado}
              >
                <Plus className="h-3.5 w-3.5" />
                Gerar primeiro contrato
              </Button>
            </WorkflowGuardGate>
          ) : undefined
        }
      />

      {/* Wizard de geração de contrato — só monta com permissão de criar */}
      {canCreate && (
        <ContractWizardDialog
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          dealId={dealId}
          tenantId={tenantId}
          contactId={contactId}
        />
      )}

      {/* Sheet de detalhes do contrato (canEdit/canDelete repassados) */}
      <ContractDetailSheet
        contractId={selectedContractId}
        onClose={() => setSelectedContractId(null)}
        canEdit={canEdit}
        canDelete={canDelete}
      />
    </div>
  );
}
